import { FunctionComponent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { faColumns3, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import { AnalyticsSelection } from "../hooks/useAnalyticsSelection";
import { COMPARISON_PARAM_PREFIX } from "../utils/analyticsUrlState";

import { ComparisonColumn } from "./ComparisonColumn";
import { DeepDiveColumn } from "./DeepDiveColumn";

/** `?compare=1` — keeps a side-by-side view shareable and survives a refresh. */
export const ANALYTICS_COMPARE_PARAM_KEY = "compare";

export interface CampaignDeepDiveTabProps {
	selection: AnalyticsSelection;
}

/**
 * The single-selection report, optionally shown twice side by side. Each column owns
 * its own lists / campaigns / date range and fetches independently, so two campaigns
 * (or the same campaign over two periods) can be read against each other.
 */
export const CampaignDeepDiveTab: FunctionComponent<CampaignDeepDiveTabProps> = ({ selection }) => {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	const isComparing = searchParams.get(ANALYTICS_COMPARE_PARAM_KEY) === "1";

	const toggleCompare = useCallback(() => {
		const next = new URLSearchParams(searchParams);

		if (isComparing) {
			next.delete(ANALYTICS_COMPARE_PARAM_KEY);
			// Drop the second column's state with it, so re-opening compare starts clean
			// instead of resurrecting a selection the user closed.
			for (const key of [...next.keys()]) {
				if (key.startsWith(COMPARISON_PARAM_PREFIX)) {
					next.delete(key);
				}
			}
		} else {
			next.set(ANALYTICS_COMPARE_PARAM_KEY, "1");
		}

		setSearchParams(next, { replace: true });
	}, [isComparing, searchParams, setSearchParams]);

	const columnHeader = useMemo(
		() => (label: string, accentClass: string) => (
			<div className="flex flex-row items-center gap-2">
				<span className={classNames("w-2 h-2 rounded-full", accentClass)} />
				<span className="text-xs font-semibold uppercase tracking-wide text-text-meta">
					{label}
				</span>
			</div>
		),
		[]
	);

	return (
		<div className="flex flex-col w-full">
			<div className="flex flex-row justify-end w-full mb-2">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-1.5 w-3 h-3" icon={isComparing ? faXmark : faColumns3} />}
					size={ButtonSize.S}
					title={t(isComparing ? "analytics.compare.stop" : "analytics.compare.start")}
					type="button"
					onClick={toggleCompare}
				/>
			</div>

			{isComparing ? (
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full items-start">
					<DeepDiveColumn
						isComparing
						header={columnHeader(t("analytics.compare.columnA"), "bg-blue-action")}
						selection={selection}
					/>
					<ComparisonColumn
						header={columnHeader(t("analytics.compare.columnB"), "bg-status-warning")}
					/>
				</div>
			) : (
				<DeepDiveColumn selection={selection} />
			)}
		</div>
	);
};
